/**
 * Passo "Defina sua nova senha" da tela de login — aparece logo após entrar
 * com a senha provisória. Não pede a senha atual: ela acabou de ser digitada.
 */
import { useState } from "react";
import { Eye, EyeOff, KeyRound, Lock, ArrowRight, Check } from "lucide-react";
import api, { formatApiError } from "../../utils/api";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { accentBg } from "../../utils/accent";

const REGRAS = [
  ["Pelo menos 8 caracteres", (s) => s.length >= 8],
  ["Uma letra maiúscula", (s) => /[A-Z]/.test(s)],
  ["Um número", (s) => /\d/.test(s)],
  ["Um caractere especial (ex.: ! @ # $)", (s) => /[^A-Za-z0-9]/.test(s)],
];

export default function DefinirNovaSenha({ email, onDone, onCancel }) {
  const [nova, setNova] = useState("");
  const [confirma, setConfirma] = useState("");
  const [show, setShow] = useState(false);
  const [erro, setErro] = useState("");
  const [salvando, setSalvando] = useState(false);

  const regrasOk = REGRAS.every(([, ok]) => ok(nova));
  const confere = nova && nova === confirma;
  const valido = regrasOk && confere;

  const salvar = async (e) => {
    e.preventDefault();
    if (!valido) return;
    setErro("");
    setSalvando(true);
    try {
      await api.post("/auth/change-password", { new_password: nova });
      await onDone();
    } catch (err) {
      setErro(formatApiError(err.response?.data?.detail) || "Não foi possível salvar a senha.");
      setSalvando(false);
    }
  };

  return (
    <div data-testid="definir-senha-step">
      <div className="flex items-center gap-2 mb-2">
        <span className="h-9 w-9 rounded-full flex items-center justify-center text-white" style={accentBg()}><KeyRound size={16} /></span>
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Defina sua nova senha</h1>
      </div>
      <p className="text-gray-500 mb-8">
        Seu acesso <span className="font-medium text-gray-700">{email}</span> foi criado com uma senha provisória. Escolha uma senha sua para continuar.
      </p>

      {erro && (
        <div className="mb-6 p-4 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="definir-senha-erro">{erro}</div>
      )}

      <form onSubmit={salvar} noValidate className="space-y-5">
        <div>
          <Label htmlFor="nova-senha" className="text-gray-700 text-sm font-medium mb-2 block">Nova senha</Label>
          <div className="relative">
            <Lock size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            <Input
              id="nova-senha"
              type={show ? "text" : "password"}
              autoFocus
              autoComplete="new-password"
              value={nova}
              onChange={(e) => setNova(e.target.value)}
              className="h-12 rounded-xl border-gray-200 accent-ring pl-11 pr-12 bg-white"
              data-testid="definir-senha-nova"
            />
            <button type="button" tabIndex={-1} onClick={() => setShow((v) => !v)} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" data-testid="definir-senha-toggle">
              {show ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          <ul className="mt-2 space-y-1">
            {REGRAS.map(([txt, ok]) => {
              const on = ok(nova);
              return (
                <li key={txt} className={`flex items-center gap-1.5 text-xs ${on ? "text-emerald-600" : "text-gray-400"}`}>
                  <Check size={12} className={on ? "" : "opacity-30"} />{txt}
                </li>
              );
            })}
          </ul>
        </div>

        <div>
          <Label htmlFor="confirma-senha" className="text-gray-700 text-sm font-medium mb-2 block">Repita a nova senha</Label>
          <div className="relative">
            <Lock size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            <Input
              id="confirma-senha"
              type={show ? "text" : "password"}
              autoComplete="new-password"
              value={confirma}
              onChange={(e) => setConfirma(e.target.value)}
              className="h-12 rounded-xl border-gray-200 accent-ring pl-11 bg-white"
              data-testid="definir-senha-confirma"
            />
          </div>
          {confirma && !confere && <p className="text-xs text-red-600 mt-1.5" data-testid="definir-senha-nao-confere">As senhas não conferem.</p>}
        </div>

        <Button type="submit" disabled={!valido || salvando} className="w-full h-12 rounded-xl text-white font-semibold text-sm" style={accentBg()} data-testid="definir-senha-salvar">
          {salvando ? (
            <span className="flex items-center gap-2"><span className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Salvando...</span>
          ) : (
            <span className="flex items-center justify-center gap-2">Salvar e entrar<ArrowRight size={16} /></span>
          )}
        </Button>
        <button type="button" onClick={onCancel} className="w-full text-center text-sm text-gray-500 hover:text-gray-700 hover:underline" data-testid="definir-senha-sair">
          Entrar com outra conta
        </button>
      </form>
    </div>
  );
}
